/* eslint-disable qwik/no-use-visible-task */
import { component$, Slot, useSignal, useStyles$, useTask$ } from "@builder.io/qwik";
import { cn } from "../../utils/tailwind-helper";

export const AnimatedCounter = component$(({ current }: { current: number }) => {
    const currentSig = useSignal<number>(current);
    const prevSig = useSignal<number>(current);
    const isFirstRender = useSignal<boolean>(true);
    const isAnimating = useSignal<boolean>(false);

    useTask$(({ track }) => {
        const newValue = track(() => current);

        // Skip animation on first render
        if (isFirstRender.value) {
            isFirstRender.value = false;
            currentSig.value = newValue;
            prevSig.value = newValue;
            return;
        }

        if (newValue !== currentSig.value) {
            // Store the previous value
            prevSig.value = currentSig.value;
            // Update to the new value
            currentSig.value = newValue;
            // Trigger animation
            isAnimating.value = true;
        }
    });

    useStyles$(`
@keyframes slideOutUp {
        from { transform: translateY(0); }
        to { transform: translateY(-28px); }
    }
@keyframes slideInUp {
    from { transform: translateY(28px); }
    to { transform: translateY(0); }
}
    `);

    return (
        <div class="relative h-6 w-6 overflow-hidden">
            {/* Only show the regular display initially or when not animating */}
            {!isAnimating.value && (
                <div class="absolute flex h-6 w-full items-center justify-center text-center">{currentSig.value}</div>
            )}

            {/* Show the animation elements when animating */}
            {isAnimating.value && (
                <div class="relative h-full w-full">
                    {/* Previous number (slides up and out) */}
                    <div
                        class="absolute flex h-6 w-full items-center justify-center text-center"
                        style={`animation: slideOutUp 300ms forwards;`}
                        onAnimationEnd$={() => {
                            isAnimating.value = false;
                        }}
                    >
                        {prevSig.value}
                    </div>

                    {/* New number (slides in from bottom) */}
                    <div
                        class="absolute flex h-6 w-full items-center justify-center text-center"
                        style={`animation: slideInUp 300ms forwards;`}
                    >
                        {currentSig.value}
                    </div>
                </div>
            )}
        </div>
    );
});

// Main component that handles reactivity and state
export const SliderIndexDisplay = component$(
    ({ currentActiveImageIndex, totalCount }: { currentActiveImageIndex: number; totalCount: number }) => {
        return (
            <div class="main relative h-full w-full">
                <div class="main-wrapper"></div>
                <div
                    class={cn(
                        "absolute inset-0 z-[31] ml-auto flex flex-col items-end gap-5 self-end p-8",
                        "pointer-events-none",
                    )}
                >
                    <div
                        class={cn(
                            "flex items-center gap-2 bg-transparent",
                            "text-base font-normal leading-[normal] text-white",
                            "pointer-events-all",
                        )}
                    >
                        {/* Use the animation component */}
                        <AnimatedCounter current={currentActiveImageIndex} />

                        <div class="h-[1px] w-[12px] bg-white" />
                        <span>{totalCount}</span>
                        <Slot />
                    </div>
                    <div class="h-[73px]"></div>
                </div>
            </div>
        );
    },
);
